/**
 * AIAssistant.tsx — Right-rail project assistant.
 *
 * The prototype's canned-reply chat was removed (2026-07-24 readiness
 * decision: no fabricated AI answers next to real data). Until the real
 * project-context assistant lands (tender-api backed), this rail is an honest
 * "In arrivo" panel that points to the REAL AI chat in Esplora KB
 * (wiki-llm-service /v1/query).
 */
import { useNavigate } from "react-router-dom";

import { I } from "../icons";
import { contextLabel } from "../../lib/nav";

interface AIAssistantProps {
  context: string;
  visible: boolean;
  onToggle: () => void;
}

export function AIAssistant({ context, visible, onToggle }: AIAssistantProps): JSX.Element {
  const navigate = useNavigate();

  if (!visible) {
    return (
      <button
        onClick={onToggle}
        title="Assistente AI — in arrivo"
        style={{
          position: "absolute",
          right: 14,
          bottom: 14,
          zIndex: 20,
          appearance: "none",
          border: "1px solid var(--c-line-strong)",
          cursor: "pointer",
          background: "var(--c-bg-elev)",
          color: "var(--c-ai)",
          padding: "8px 12px",
          borderRadius: 999,
          fontSize: 12.5,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "var(--sh-2)",
        }}
      >
        <I.Sparkle size={12} />
        Assistente AI
      </button>
    );
  }

  return (
    <aside
      style={{
        width: 320,
        flexShrink: 0,
        borderLeft: "1px solid var(--c-line)",
        background: "var(--c-bg-elev)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          borderBottom: "1px solid var(--c-line)",
        }}
      >
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            flexShrink: 0,
            background: "var(--c-ai-soft)",
            color: "var(--c-ai)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <I.Sparkle size={14} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-ink)" }}>Assistente CME</div>
          <div style={{ fontSize: 11, color: "var(--c-ink-3)" }}>
            Contesto:{" "}
            <span style={{ color: "var(--c-ai)", fontWeight: 500 }}>{contextLabel(context)}</span>
          </div>
        </div>
        <button className="btn btn-ghost btn-icon btn-sm" title="Chiudi" onClick={onToggle}>
          <I.X size={13} />
        </button>
      </div>

      {/* In arrivo */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 12,
          padding: "24px 22px",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "var(--c-ai-soft)",
            color: "var(--c-ai)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <I.Sparkle size={18} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--c-ink)" }}>
            Assistente di progetto
          </span>
          <span className="pill pill-info" style={{ height: 18, padding: "0 7px", fontSize: 10.5 }}>
            In arrivo
          </span>
        </div>
        <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-ink-2)" }}>
          Qui potrai fare domande sul progetto — discrepanze, prezzi, categorie — con risposte
          basate sui dati reali dell&apos;analisi.
        </p>
        <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: "var(--c-ink-3)" }}>
          Nel frattempo puoi usare la chat AI della Knowledge Base:
        </p>
        <button className="btn btn-sm" onClick={() => navigate("/kb/explore")}>
          <I.Network size={13} /> Apri Esplora KB
        </button>
      </div>
    </aside>
  );
}
